import React, { useState, useCallback } from 'react';
import { Button } from '../ui/button';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { AlertCircle, RefreshCw, Shield, Clock, Wifi, Settings } from 'lucide-react';
import { RBACError, RBACErrorCode } from '../../stores/rbac-store';
import { auditLogger, AuditEventType } from '../../services/auditLogger';

interface RBACErrorHandlerProps {
  error: RBACError | null;
  retryCount: number;
  maxRetries: number;
  isRetrying: boolean;
  onRetry: () => void;
  onClearError: () => void;
  className?: string;
  enableAutoRetry?: boolean;
  autoRetryInterval?: number;
  showDetailedDiagnostics?: boolean;
  onFallbackMode?: () => void;
}

/**
 * Component for handling RBAC errors with user-friendly messages and retry functionality
 */
export const RBACErrorHandler: React.FC<RBACErrorHandlerProps> = ({
  error,
  retryCount,
  maxRetries,
  isRetrying,
  onRetry,
  onClearError,
  className = '',
  enableAutoRetry = false,
  autoRetryInterval = 5000,
  showDetailedDiagnostics = false,
  onFallbackMode
}) => {
  const [autoRetryTimer, setAutoRetryTimer] = useState<NodeJS.Timeout | null>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const [fallbackModeEnabled, setFallbackModeEnabled] = useState(false);
  // Enhanced retry with exponential backoff
  const handleEnhancedRetry = useCallback(() => {
    // Log retry attempt
    auditLogger.logError(
      AuditEventType.ERROR,
      error?.code || RBACErrorCode.INITIALIZATION_ERROR,
      undefined,
      { retryCount, action: 'manual_retry' }
    );
    
    onRetry();
  }, [error, retryCount, onRetry]);

  // Auto-retry with exponential backoff
  React.useEffect(() => {
    if (enableAutoRetry && error && retryCount < maxRetries && !isRetrying) {
      const delay = Math.min(autoRetryInterval * Math.pow(2, retryCount), 30000);
      
      const timer = setTimeout(() => {
        auditLogger.logError(
          AuditEventType.ERROR,
          error.code,
          undefined,
          { retryCount, action: 'auto_retry', delay }
        );
        onRetry();
      }, delay);
      
      setAutoRetryTimer(timer);
      
      return () => {
        if (timer) clearTimeout(timer);
      };
    }
  }, [enableAutoRetry, error, retryCount, maxRetries, isRetrying, autoRetryInterval, onRetry]);

  // Fallback mode activation
  const handleFallbackMode = useCallback(() => {
    setFallbackModeEnabled(true);
    auditLogger.log({
      eventType: AuditEventType.ERROR,
      result: 'GRANTED',
      details: { action: 'fallback_mode_enabled', errorCode: error?.code }
    });
    onFallbackMode?.();
  }, [error, onFallbackMode]);

  if (!error) return null;

  const getErrorIcon = () => {
    switch (error.code) {
      case RBACErrorCode.NO_ROLE:
        return <Shield className="h-4 w-4" />;
      case RBACErrorCode.INITIALIZATION_ERROR:
        return <AlertCircle className="h-4 w-4" />;
      case RBACErrorCode.NETWORK_ERROR:
        return <Wifi className="h-4 w-4" />;
      case RBACErrorCode.INVALID_PERMISSION:
        return <Shield className="h-4 w-4" />;
      case RBACErrorCode.CACHE_ERROR:
        return <Clock className="h-4 w-4" />;
      case RBACErrorCode.AUDIT_ERROR:
        return <Settings className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };

  const getErrorTitle = () => {
    switch (error.code) {
      case RBACErrorCode.NO_ROLE:
        return 'Missing User Role';
      case RBACErrorCode.INITIALIZATION_ERROR:
        return 'Initialization Failed';
      case RBACErrorCode.NETWORK_ERROR:
        return 'Network Error';
      case RBACErrorCode.INVALID_PERMISSION:
        return 'Invalid Permissions';
      case RBACErrorCode.CACHE_ERROR:
        return 'Cache Error';
      case RBACErrorCode.AUDIT_ERROR:
        return 'Audit System Error';
      default:
        return 'Permission Error';
    }
  };

  const getErrorMessage = () => {
    switch (error.code) {
      case RBACErrorCode.NO_ROLE:
        return 'Your account is missing required role information. Please contact your administrator.';
      case RBACErrorCode.INITIALIZATION_ERROR:
        return 'Failed to initialize user permissions. Please try refreshing the page.';
      case RBACErrorCode.NETWORK_ERROR:
        return 'Network error while loading permissions. Please check your connection and try again.';
      case RBACErrorCode.INVALID_PERMISSION:
        return 'Invalid permission configuration. Please contact support.';
      case RBACErrorCode.CACHE_ERROR:
        return 'Permission cache error. Permissions will be re-validated.';
      case RBACErrorCode.AUDIT_ERROR:
        return 'Audit logging error. Permissions are still functional.';
      default:
        return error.message || 'An unexpected error occurred while loading permissions.';
    }
  };

  const getRecoveryOptions = () => {
    switch (error.code) {
      case RBACErrorCode.NO_ROLE:
        return {
          showRetry: false,
          showFallback: false,
          showContact: true
        };
      case RBACErrorCode.NETWORK_ERROR:
        return {
          showRetry: true,
          showFallback: true,
          showContact: false
        };
      case RBACErrorCode.CACHE_ERROR:
        return {
          showRetry: true,
          showFallback: false,
          showContact: false
        };
      default:
        return {
          showRetry: true,
          showFallback: !!onFallbackMode,
          showContact: retryCount >= maxRetries
        };
    }
  };

  const canRetry = retryCount < maxRetries;

  const recoveryOptions = getRecoveryOptions();

  return (
    <Alert variant={fallbackModeEnabled ? 'default' : 'destructive'} className={className}>
      {getErrorIcon()}
      <AlertTitle>
        {getErrorTitle()}
        {fallbackModeEnabled && (
          <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
            Fallback Mode
          </span>
        )}
      </AlertTitle>
      <AlertDescription className="mt-2">
        <p className="mb-3">{getErrorMessage()}</p>
        
        {canRetry && recoveryOptions.showRetry && (
          <div className="flex items-center gap-2 mb-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleEnhancedRetry}
              disabled={isRetrying}
              className="h-8"
            >
              {isRetrying ? (
                <>
                  <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-3 w-3" />
                  Retry
                </>
              )}
            </Button>
            
            <span className="text-xs text-muted-foreground">
              Attempt {retryCount + 1} of {maxRetries}
              {enableAutoRetry && autoRetryTimer && (
                <span className="ml-2">
                  (Auto-retry in {Math.ceil(autoRetryInterval * Math.pow(2, retryCount) / 1000)}s)
                </span>
              )}
            </span>
          </div>
        )}
        
        {/* Recovery options */}
        <div className="flex items-center gap-2 flex-wrap">
          {recoveryOptions.showFallback && !fallbackModeEnabled && (
            <Button
              variant="secondary"
              size="sm"
              onClick={handleFallbackMode}
              className="h-8"
            >
              Enable Fallback Mode
            </Button>
          )}
          
          {fallbackModeEnabled && (
            <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded">
              Fallback mode active
            </span>
          )}
          
          {showDetailedDiagnostics && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDiagnostics(!showDiagnostics)}
              className="h-8"
            >
              {showDiagnostics ? 'Hide' : 'Show'} Diagnostics
            </Button>
          )}
          
          {recoveryOptions.showContact && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('mailto:support@example.com', '_blank')}
              className="h-8"
            >
              Contact Support
            </Button>
          )}
        </div>
        
        {!canRetry && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={onClearError}
              className="h-8"
            >
              Dismiss
            </Button>
            
            <span className="text-xs text-muted-foreground">
              Max retries exceeded
            </span>
          </div>
        )}
        
        {/* Diagnostic information */}
        {showDiagnostics && showDetailedDiagnostics && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-medium mb-2">Diagnostic Information</h4>
            <div className="text-xs space-y-1">
              <div><strong>Error Code:</strong> {error.code}</div>
              <div><strong>Timestamp:</strong> {error.timestamp ? new Date(error.timestamp).toLocaleString() : 'N/A'}</div>
              <div><strong>User ID:</strong> {error.userId || 'N/A'}</div>
              <div><strong>Retry Count:</strong> {retryCount}</div>
              <div><strong>Auto Retry:</strong> {enableAutoRetry ? 'Enabled' : 'Disabled'}</div>
              {error.details != null && (
                <div>
                  <strong>Details:</strong>
                  <pre className="mt-1 p-2 bg-white rounded text-xs overflow-x-auto">
                    {typeof error.details === 'string' 
                      ? error.details 
                      : error.details 
                        ? JSON.stringify(error.details, null, 2) 
                        : 'No details available'
                    }
                  </pre>
                </div>
              )}
            </div>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}; 